export const CLICK_KEYS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid", "ttclid"];
// Google Ads auto-tagging params. They mark a paid Google click like gclid does.
export const GOOGLE_ADS_KEYS = ["gad_source", "gad_campaignid"];
const isCampaignKey = (key) => key.startsWith("utm_") || CLICK_KEYS.includes(key) || GOOGLE_ADS_KEYS.includes(key);

const UTM_KEY = /^utm_[a-z][a-z0-9_]{0,31}$/;
const CREATOR_CODE = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const CTA_SOURCE = /^[a-z0-9][a-z0-9_-]{0,63}$/;

const asTouch = (value) => (value && typeof value === "object" && !Array.isArray(value) ? value : {});

export function normalizeCtaSource(value) {
  const token = String(value || "").trim().toLowerCase();
  return CTA_SOURCE.test(token) ? token : "";
}

export function normalizeCreatorCode(value) {
  const code = String(value || "").trim().toLowerCase();
  return CREATOR_CODE.test(code) ? code : "";
}

// First-touch story token from ?story=. Any value is kept after it is
// lowercased, stripped to letters, numbers, underscore, and hyphen, and capped.
// An address is dropped so the touch cannot carry mail.
export function normalizeStory(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw || raw.includes("@")) return "";
  return raw.replace(/[^a-z0-9_-]/g, "").slice(0, 64);
}

function creatorFromLocation(search, pathname) {
  const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));
  const fromQuery = normalizeCreatorCode(params.get("ref") || "");
  if (fromQuery) return fromQuery;
  const match = String(pathname || "").match(/^\/(?:r|c)\/([^/]+)\/?$/);
  if (!match) return "";
  try {
    return normalizeCreatorCode(decodeURIComponent(match[1]));
  } catch {
    return normalizeCreatorCode(match[1]);
  }
}

export function applyCreatorCode(touch, code) {
  const next = { ...asTouch(touch) };
  const normalized = normalizeCreatorCode(code);
  if (normalized && !next.creator_code) next.creator_code = normalized;
  return next;
}

function copyCreatorCode(target, source) {
  if (source.creator_code && !target.creator_code) target.creator_code = source.creator_code;
  return target;
}

function copyStory(target, source) {
  if (source.story && !target.story) target.story = source.story;
  return target;
}

function copyCtaSource(target, source) {
  if (source.cta_source && !target.cta_source) target.cta_source = source.cta_source;
  return target;
}

export function hasCampaignSignal(touch) {
  return Object.keys(asTouch(touch)).some(isCampaignKey);
}

function campaignFields(touch) {
  return Object.fromEntries(
    Object.entries(asTouch(touch)).filter(([key]) => isCampaignKey(key))
  );
}

function applyGoogleAdsSource(touch) {
  if (!touch.gclid && !touch.gbraid && !touch.wbraid && !touch.gad_source && !touch.gad_campaignid) return touch;
  if (touch.utm_source) return touch;
  touch.utm_source = "google_ads";
  if (!touch.utm_medium) touch.utm_medium = "cpc";
  return touch;
}

function applyQuery(touch, search) {
  const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));
  params.forEach((value, rawKey) => {
    const key = String(rawKey || "").trim().toLowerCase();
    const text = String(value || "").trim().slice(0, 500);
    if (!text) return;
    if (UTM_KEY.test(key) || CLICK_KEYS.includes(key) || GOOGLE_ADS_KEYS.includes(key)) touch[key] = text;
  });
}

export function collectTouch({ search = "", pathname = "", referrer = "", currentHost = "" } = {}) {
  const touch = {};
  if (pathname) touch.landing_path = String(pathname).slice(0, 500);
  applyQuery(touch, search);
  const creator = creatorFromLocation(search, pathname);
  if (creator) touch.creator_code = creator;
  const pageParams = new URLSearchParams(String(search || "").replace(/^\?/, ""));
  const story = normalizeStory(pageParams.get("story"));
  if (story) touch.story = story;
  const ctaSource = normalizeCtaSource(pageParams.get("cta_source"));
  if (ctaSource) touch.cta_source = ctaSource;
  if (!hasCampaignSignal(touch) && referrer) {
    try {
      const url = new URL(referrer);
      if (url.hostname && url.hostname !== currentHost) applyQuery(touch, url.search || "");
    } catch {
      // Invalid referrers are ignored.
    }
  }
  if (referrer) {
    try {
      const url = new URL(referrer);
      if (url.hostname && url.hostname !== currentHost) touch.referrer_host = url.hostname.slice(0, 500);
    } catch {
      // Invalid referrers are ignored.
    }
  }
  return applyGoogleAdsSource(touch);
}

export function mergeFirstTouch(existing, observed) {
  const current = { ...asTouch(existing) };
  const next = asTouch(observed);
  let merged;
  if (hasCampaignSignal(current)) {
    merged = { ...current };
  } else if (hasCampaignSignal(next)) {
    merged = { ...current, ...next };
    if (current.story) merged.story = current.story;
    if (current.creator_code) merged.creator_code = current.creator_code;
    if (current.cta_source) merged.cta_source = current.cta_source;
  } else if (Object.keys(current).length) {
    merged = { ...current };
  } else {
    merged = { ...next };
  }
  return copyCtaSource(copyStory(copyCreatorCode(merged, next), next), next);
}

// Auth pages fill holes in a stored first touch. They do not replace a key
// that already has a value, and they do not adopt the auth URL as the landing.
export function mergeAuthFirstTouch(existing, observed) {
  const current = { ...asTouch(existing) };
  const next = asTouch(observed);
  if (!Object.keys(current).length) return { ...next };
  const fill = (key) => {
    if (current[key]) return;
    if (next[key]) current[key] = next[key];
  };
  fill("cta_source");
  fill("story");
  fill("creator_code");
  CLICK_KEYS.forEach(fill);
  GOOGLE_ADS_KEYS.forEach(fill);
  Object.keys(next).forEach((key) => {
    if (key.startsWith("utm_")) fill(key);
  });
  return current;
}

export function mergeLastTouch(existing, observed) {
  const current = { ...asTouch(existing) };
  const next = asTouch(observed);
  if (!hasCampaignSignal(next)) {
    if (Object.keys(current).length || !Object.keys(next).length) return current;
    return { ...next };
  }
  if (!hasCampaignSignal(current)) return { ...next };
  const sameAsExistingCampaign = JSON.stringify(campaignFields(next)) === JSON.stringify(campaignFields(current));
  if (sameAsExistingCampaign) return current;
  return { ...next };
}
